import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { StatusPill, EmptyState, InlineLoader } from "@/components/shared";
import { StatCard } from "@/pages/organizer/OrganizerDashboard";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ListChecks, ShieldCheck, Flag, Banknote, HeartHandshake, DollarSign, ArrowRight, Inbox, Clock, AlertTriangle, Video, MessageSquarePlus } from "lucide-react";

export default function ReviewQueue() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    api.get("/admin/review-queue").then((r) => setQueue(r.data)).catch(() => setQueue([]));
  }, []);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Review Queue</h1>
        <p className="mt-1 text-sm text-ff-medium">FightFund reviews campaigns before they can accept public donations.</p>
      </div>

      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={ListChecks} label="Pending Reviews" value={stats.pending_reviews || 0} accent="red" />
          <StatCard icon={AlertTriangle} label="Overdue >48h" value={stats.overdue_reviews || 0} accent={stats.overdue_reviews ? "red" : "blue"} />
          <StatCard icon={Flag} label="Open Complaints" value={stats.open_complaints || 0} />
          <StatCard icon={DollarSign} label="Processed" value={money(stats.total_processed)} accent="green" />
        </div>
      )}

      {stats && (
        <div className="mb-8 rounded-2xl border border-ff-border bg-white p-5 shadow-sm" data-testid="trust-safety-panel">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EFF6FF] text-ff-blue"><ShieldCheck className="h-4 w-4" /></span>
            <div>
              <h2 className="font-heading text-base font-bold text-ff-blue">Trust &amp; Safety</h2>
              <p className="text-xs text-ff-medium">Everything awaiting a moderation decision, at a glance.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <SafetyTile to="/admin/complaints" icon={Flag} label="Open Complaints" count={stats.open_complaints || 0} testid="ts-tile-complaints" />
            <SafetyTile to="/admin/videos" icon={Video} label="Videos to Review" count={stats.pending_videos || 0} testid="ts-tile-videos" />
            <SafetyTile to="/admin/payouts" icon={Banknote} label="Payout Queue" count={stats.pending_payouts || 0} testid="ts-tile-payouts" />
            <SafetyTile to="/admin/complaints" icon={AlertTriangle} label="Open Risk Flags" count={stats.open_flags || 0} testid="ts-tile-flags" />
            <SafetyTile to="/admin/feedback" icon={MessageSquarePlus} label="Beta Feedback" count={stats.open_feedback || 0} testid="ts-tile-feedback" />
          </div>
        </div>
      )}

      {queue === null ? (
        <InlineLoader label="Loading review queue" />
      ) : queue.length === 0 ? (
        <EmptyState icon={Inbox} title="Review queue is clear" message="No campaigns are waiting for review right now." testid="review-queue-empty" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-ff-border bg-white shadow-sm" data-testid="admin-review-queue-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F4F6]">
                <TableHead>Campaign</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Waiting</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.map((c) => (
                <TableRow key={c.id} data-testid={`review-row-${c.slug}`} className={c.overdue ? "bg-[#FEF2F2]/40" : ""}>
                  <TableCell>
                    <div className="font-heading font-bold text-ff-blue">{c.title}</div>
                    <div className="text-xs text-ff-medium">Goal: {c.goal_amount ? money(c.goal_amount) : "—"}</div>
                  </TableCell>
                  <TableCell>{c.category}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.risk_tier === "high" ? "bg-[#FEF2F2] text-ff-red" : c.risk_tier === "elevated" ? "bg-[#FFF7ED] text-ff-amber" : "bg-[#F3F4F6] text-ff-dark"}`}>
                      {c.risk_tier || "standard"}{c.risk_score != null ? ` · ${c.risk_score}` : ""}
                    </span>
                  </TableCell>
                  <TableCell>{c.open_flags > 0 ? <span className="font-bold text-ff-red">{c.open_flags}</span> : "0"}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${c.overdue ? "text-ff-red" : "text-ff-medium"}`}>
                      <Clock className="h-3 w-3" /> {c.hours_in_queue != null ? `${c.hours_in_queue}h` : "—"}
                    </span>
                  </TableCell>
                  <TableCell><StatusPill status={c.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" className="bg-ff-blue text-white hover:bg-ff-blue/90">
                      <Link to={`/admin/reviews/${c.id}`} data-testid={`review-open-${c.slug}`}>Review <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function SafetyTile({ to, icon: Icon, label, count, testid }) {
  const active = count > 0;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group flex items-center justify-between rounded-xl border p-4 transition-colors duration-200 ${
        active ? "border-ff-red/30 bg-[#FEF2F2] hover:border-ff-red" : "border-ff-border bg-[#F3F4F6] hover:border-ff-blue"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${active ? "bg-ff-red/10 text-ff-red" : "bg-white text-ff-medium"}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className={`font-heading text-2xl font-extrabold leading-none ${active ? "text-ff-red" : "text-ff-blue"}`} data-testid={`${testid}-count`}>{count}</div>
          <div className="mt-1 text-xs font-semibold text-ff-medium">{label}</div>
        </div>
      </div>
      <ArrowRight className={`h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 ${active ? "text-ff-red" : "text-ff-medium"}`} />
    </Link>
  );
}
